import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Link } from "@/components/LocalLink";
import ReactMarkdown from "react-markdown";
import { useLang } from "@/i18n/LanguageContext";
import { BuyButton } from "@/components/BuyButton";
import { LegacyShell } from "@/components/PageShell";
import { ShareBar } from "@/components/ShareBar";
import { PRODUCTS, formatCad } from "@/data/catalog";
import { API, ArticleCard, articleImage, pick, fmtDate, setMeta, usePublished } from "@/components/JournalParts";

const SITE = "https://dirtypawsapothecary.com";

const md = {
  h2: ({ children }) => <h2 className="mt-14 mb-5 font-display text-3xl text-[#E8DDC7] leading-snug">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-10 mb-4 font-display text-2xl text-[#E8DDC7]">{children}</h3>,
  p: ({ children }) => <p className="mb-6 text-[17px] leading-[1.85] text-[#D3CEC4]/80">{children}</p>,
  ul: ({ children }) => <ul className="mb-6 pl-5 list-disc marker:text-[#B5A642] space-y-2 text-[17px] leading-relaxed text-[#D3CEC4]/80">{children}</ul>,
  ol: ({ children }) => <ol className="mb-6 pl-5 list-decimal marker:text-[#B5A642] space-y-2 text-[17px] leading-relaxed text-[#D3CEC4]/80">{children}</ol>,
  blockquote: ({ children }) => <blockquote className="my-8 border-l border-[#B5A642] pl-6 font-display italic text-2xl text-[#E8DDC7]/90">{children}</blockquote>,
  a: ({ href, children }) => <a href={href} className="text-[#B5A642] underline underline-offset-4 hover:text-[#E8DDC7] transition-colors">{children}</a>,
  strong: ({ children }) => <strong className="text-[#E8DDC7] font-semibold">{children}</strong>,
};

const ProductPlate = ({ product, t }) => {
  return (
    <aside data-testid="article-product" className="my-16 grid sm:grid-cols-5 gap-6 items-center border border-[#B5A642]/30 rounded-sm p-6 bg-[#161918]">
      <img src={product.image} alt={product.name} className="sm:col-span-2 w-full aspect-[4/5] object-cover rounded-sm" />
      <div className="sm:col-span-3">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#B5A642]/80">{t("From the Pawthecary", "De la Pawthécaire")}</p>
        <p className="mt-2 font-display text-3xl text-[#E8DDC7]">{product.name}</p>
        <p className="mt-1 font-display italic text-[#D3CEC4]/50">{product.latin}</p>
        <p className="mt-3 text-[14px] leading-relaxed text-[#D3CEC4]/65">{product.blurb}</p>
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <BuyButton productIds={product.id} testid="article-add-to-cart" className="btn-primary" />
          <Link to={`/products/${product.slug}`} data-testid="article-product-link" className="text-[13px] font-medium text-[#D2C08E] hover:text-[#E8DDC7] transition-colors">
            {t("See the jar", "Voir le pot")} · {formatCad(product.priceCents)}
          </Link>
        </div>
      </div>
    </aside>
  );
};

export default function Article() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [a, setA] = useState(undefined);
  const more = usePublished();

  useEffect(() => {
    window.scrollTo(0, 0);
    setA(undefined);
    axios.get(`${API}/journal/${slug}`).then((r) => setA(r.data)).catch(() => setA(null));
  }, [slug]);

  const c = a ? pick(a, lang) : null;
  useEffect(() => {
    if (!a) return undefined;
    const undo = setMeta(`${c.title} — The Journal · Dirty Paws Apothecary`, c.meta_description || c.dek);
    const ld = Object.assign(document.createElement("script"), { type: "application/ld+json", id: "article-schema" });
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "Article", headline: c.title, description: c.meta_description || c.dek,
      image: `${SITE}/api/journal/image/${a.id}`, datePublished: a.published_at, inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
      author: { "@type": "Organization", name: "Dirty Paws Apothecary" },
      publisher: { "@type": "Organization", name: "Dirty Paws Apothecary", logo: { "@type": "ImageObject", url: `${SITE}/apple-touch-icon.png` } },
      mainEntityOfPage: `${SITE}/journal/${a.slug}`, keywords: (c.keywords || []).join(", "),
    });
    document.head.appendChild(ld);
    return () => { undo(); ld.remove(); };
  }, [a, c, lang]);

  const product = a && PRODUCTS.find((p) => p.id === a.product_id);
  const others = (more || []).filter((x) => x.slug !== slug).slice(0, 2);

  return (
    <LegacyShell testid="article-page">
      {a === null && (
        <main className="max-w-3xl mx-auto px-5 py-28 text-center" data-no-translate>
          <p data-testid="article-not-found" className="font-display italic text-3xl text-[#D3CEC4]/60">{t("This page has wandered off into the woods.", "Cette page s'est égarée dans la forêt.")}</p>
          <Link to="/journal" className="mt-6 inline-block text-[11px] uppercase tracking-[0.2em] text-[#B5A642]">{t("Back to the Journal", "Retour au Journal")}</Link>
        </main>
      )}
      {a && (
        <article className="pb-20" data-no-translate>
          <div className="max-w-3xl mx-auto px-5 md:px-10 pt-20 md:pt-28">
            <Link to="/journal" data-testid="article-journal-link" className="font-display italic text-[#B5A642] hover:text-[#E8DDC7] transition-colors">{t("The Journal", "Le Journal")}</Link>
            <h1 data-testid="article-title" className="mt-4 font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[#E8DDC7] leading-[1.08]">{c.title}</h1>
            <p className="mt-6 text-lg md:text-xl leading-relaxed text-[#D3CEC4]/65 font-display italic">{c.dek}</p>
            <p className="mt-6 font-ledger text-[11px] text-[#D3CEC4]/45">{fmtDate(a.published_at, lang)} · {t("The keepers of the house", "Les gardiens de la maison")}</p>
            <div className="mt-8"><ShareBar slug={a.slug} title={c.title} lang={lang} t={t} /></div>
          </div>
          <div className="max-w-5xl mx-auto px-5 md:px-10 mt-12">
            <img src={articleImage(a)} alt={c.title} data-testid="article-image" className="w-full aspect-[16/9] object-cover rounded-sm border border-[#303B2D]/60" />
          </div>
          <div className="max-w-3xl mx-auto px-5 md:px-10 mt-14 dropcap-article" data-testid="article-body">
            <ReactMarkdown components={md}>{c.body_md}</ReactMarkdown>
            {product && <ProductPlate product={product} t={t} />}
            <div className="mb-10"><ShareBar slug={a.slug} title={c.title} lang={lang} t={t} /></div>
            <p className="font-ledger text-[11px] leading-relaxed text-[#D3CEC4]/45">
              {t("A word of care: this article is for general interest and is not veterinary advice. Speak with your veterinarian before adding anything new to your dog's routine.",
                "Un mot de prudence : cet article est d'intérêt général et ne constitue pas un avis vétérinaire. Parlez à votre vétérinaire avant d'ajouter quoi que ce soit à la routine de votre chien.")}
            </p>
          </div>
          {others.length > 0 && (
            <div className="max-w-5xl mx-auto px-5 md:px-10 mt-24">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#B5A642]/80 mb-8">{t("Further reading", "À lire aussi")}</p>
              <div className="grid sm:grid-cols-2 gap-10">{others.map((x) => <ArticleCard key={x.id} a={x} lang={lang} />)}</div>
            </div>
          )}
        </article>
      )}
    </LegacyShell>
  );
}
